import React, { useContext } from 'react';

import { Capsule } from 'Components/frameworks/capsule/Capsule';
import { type PlanIframeProps } from 'Components/widgets/payment-info/buy-now-pay-later/BuyNowPayLater';
import { useFormikContext } from 'formik';
import type {
  PaymentBookingResumeAttendee,
  PaymentFormValues,
  UpliftBuildOrderParams,
} from 'Src/types/payment';
import { defaultGqlQueryOptions } from 'Src/types/payment';
import { PaymentInfoContext } from 'Components/widgets/payment-info/PaymentInfoContext';
import { useGQL } from 'Utils/graphql-client/use-gql/useGQL';
import { useAppContext } from 'Context/ReactAppContext';
import { useRouter } from 'next/router';
import { extractPayload } from 'Views/account/pre-payment/PrePaymentView.utils';
import URI from 'urijs';
import { useSelector } from 'react-redux';
import { useUplift } from './useUplift';
import { trackUplift } from './UpliftForm.utils';

const getAttendeesQuery = (
  attendeesId: ReadonlyArray<string | PaymentBookingResumeAttendee>,
) => `query { 
  attendees(attendeesIds: ["${attendeesId.join('","')}"]) {
          firstName
          lastName
          dateOfBirth
          mobilePhoneNumber
          email
          address {
            streetNumber
            streetName
            zipCode
            city
            stateOrDistrict
            countryCode
          }
      }
}`;

export const UpliftForm = ({ onLoaded, price, display }: PlanIframeProps) => {
  const {
    onBuyNowPayLaterComplete,
    currency,
    bookingResume: {
      attendees: attendeesResume = [],
      accommodations,
      transports,
      departureDate,
      arrivalDate,
      name,
    },
  } = useContext(PaymentInfoContext);
  const formik = useFormikContext<PaymentFormValues>();
  const router = useRouter();
  const {
    query: { a, bnpl, paymentStatus },
  } = router;

  const isProposalLoading = useSelector((state) => state.booking.isProposalLoading);

  const attendees = extractPayload(Array.isArray(a) ? a[0] : a) || [];

  const { isUserLogged, isUserQuickLogged } = useAppContext();

  const onTokenAvailable = () => {
    onBuyNowPayLaterComplete();
  };
  const onTokenRetrieved = (token: string) => {
    if (token) {
      formik.setFieldValue('token', token);
      formik.submitForm();
    }
  };

  const { data, isLoading: isAttendeesLoading } = useGQL<{ attendees?: unknown[] }>({
    query: getAttendeesQuery(
      attendeesResume.map((attendee) =>
        typeof attendee === 'string' ? attendee : attendee.email || '',
      ),
    ),
    ignore: !isUserLogged || attendeesResume.length === 0,
    cache: true,
    queryOptions: defaultGqlQueryOptions,
  });

  const booking: UpliftBuildOrderParams = {
    price,
    attendees: data?.attendees || (isUserQuickLogged ? attendeesResume : attendees) || [],
    accommodations,
    transports,
    departureDate,
    arrivalDate,
    name,
  };

  const { shouldDisplay } = useUplift({
    price,
    display,
    shouldInit: !isAttendeesLoading && !isProposalLoading,
    isPaymentPage: true,
    isReadyToDisplay: display,
    booking,
    currency,
    onTokenAvailable,
    onTokenRetrieved,
    onLoaded: (loaded: boolean) => {
      onLoaded(loaded);
      if (bnpl && paymentStatus) {
        trackUplift({ orderId: '', isErrored: true, errorMsg: String(paymentStatus) });
        const newUri = new URI(router.asPath).removeQuery(['bnpl']);
        router.replace(newUri.toString()).catch(() => {});
      }
    },
  });

  return (
    <Capsule className={`${shouldDisplay ? 'block' : 'hidden'} my-40`}>
      <div id="uplift-container"></div>
    </Capsule>
  );
};
